import { useQuery } from '@tanstack/react-query';
import type { SoaVersion } from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { Badge, Button, Card, Spinner, formatDate } from '../../components/ui.js';
import type { ChangesTarget } from './SoaDialogs.js';
import { errorMessage } from './soaModel.js';

/**
 * Side panel listing the recorded versions (newest first) with their change
 * counts, plus the working copy's changes since the latest version.
 */
export function SoaVersionsPanel({
  orgId,
  versionId,
  latest,
  onViewChanges,
  onClose,
}: {
  orgId: string;
  versionId: string;
  latest: SoaVersion | null;
  onViewChanges: (target: ChangesTarget) => void;
  onClose: () => void;
}) {
  const versions = useQuery({
    queryKey: ['soa-versions', orgId, versionId],
    queryFn: () => apiFetch<SoaVersion[]>(`/orgs/${orgId}/soa/${versionId}/versions`),
  });

  return (
    <aside className="soa-versions" aria-label="Recorded versions">
      <Card
        title="Versions"
        actions={
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        }
      >
        {latest && (
          <div className="soa-version soa-version-current">
            <div className="soa-version-head">
              <strong>Working copy</strong>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  onViewChanges({
                    soaVersionId: null,
                    title: `Changes since version ${latest.label}`,
                  })
                }
              >
                Changes since {latest.label}
              </Button>
            </div>
            <span className="soa-muted">
              Cells changed since the latest recorded version — what the next version would contain.
            </span>
          </div>
        )}

        {versions.isPending ? (
          <div className="soa-panel-loading">
            <Spinner label="Loading versions" />
          </div>
        ) : versions.isError ? (
          <p className="soa-muted">{errorMessage(versions.error, 'Could not load the versions.')}</p>
        ) : versions.data.length === 0 ? (
          <p className="soa-muted">
            No version recorded yet. Record one once the rows are in order — the export is marked
            Draft until then.
          </p>
        ) : (
          versions.data.map((version) => (
            <div key={version.id} className="soa-version">
              <div className="soa-version-head">
                <strong>Version {version.label}</strong>
                <Badge tone={version.changeCount > 0 ? 'warning' : 'neutral'}>
                  {version.changeCount} change{version.changeCount === 1 ? '' : 's'}
                </Badge>
              </div>
              <div className="soa-muted">
                {formatDate(version.createdAt)} · {version.authorName}
              </div>
              {version.reason && <div className="soa-version-reason">{version.reason}</div>}
              <div className="soa-muted">
                {version.approvedByName
                  ? `Approved by ${version.approvedByName}${
                      version.approvedOn ? ` on ${formatDate(version.approvedOn)}` : ''
                    }`
                  : 'Not yet approved'}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  onViewChanges({
                    soaVersionId: version.id,
                    title: `Version ${version.label} — changed cells`,
                  })
                }
              >
                View changes
              </Button>
            </div>
          ))
        )}
      </Card>
    </aside>
  );
}
